import type { ReactElement } from "react";
import { Navigate, useLocation } from "react-router-dom";
import type { UserRole } from "../contract.js";
import { useAppSelector } from "../store/index.js";

interface RequireAuthProps {
  /** Empty or omitted: any signed-in user. */
  roles?: UserRole[];
  children: ReactElement;
}

const ROLE_LABEL: Record<UserRole, string> = {
  admin: "Administrator",
  releaser: "Perilis",
  analyst: "Analis",
  viewer: "Peninjau",
};

export function RequireAuth({ roles, children }: RequireAuthProps) {
  const location = useLocation();
  const { accessToken, user } = useAppSelector((state) => state.auth);

  if (!accessToken || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // The seeded admin/admin account is deliberately useless until its password
  // is rotated: while the flag is set, /change-password is the only reachable
  // route, so there is no window in which a default credential can operate.
  if (user.mustChangePassword && location.pathname !== "/change-password") {
    return <Navigate to="/change-password" replace />;
  }

  if (roles && roles.length > 0 && !roles.includes(user.role)) {
    return (
      <div className="page">
        <div className="notice notice-danger" role="alert">
          <strong>Akses ditolak</strong>
          <span>
            Halaman ini membutuhkan peran {roles.map((role) => ROLE_LABEL[role]).join(" atau ")};
            Anda masuk sebagai {ROLE_LABEL[user.role]}.
          </span>
        </div>
      </div>
    );
  }

  return children;
}
